import { Upload } from "antd";
import { get, isEmpty, isNil, noop } from "lodash";
import { useEffect, useRef, useState } from "react";
import Button from "shared/components/Button";
import { useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { readFileAsBase64 } from "utils/helpers/files";
import { PDF_CONTENT_TYPE } from "./constants";
import { zonesOf } from "./field-status";

export const PDF_PHASES = {
  PICK: "pick",
  READING: "reading",
  READ_FAILED: "readFailed",
  NO_ZONE: "noZone",
  READY: "ready",
};

export const PDF_ACCEPT = `.pdf,${PDF_CONTENT_TYPE}`;

const PICKING = { phase: PDF_PHASES.PICK, file: null, anchors: [] };

export const usePdfAnchors = ({ onReady = noop } = {}) => {
  const [detectAnchors] = useMutation(
    graphql.mutations.DETECT_DOCUMENT_ANCHORS,
    { onError: noop },
  );
  const attemptRef = useRef(0);
  const [state, setState] = useState(PICKING);

  useEffect(
    () => () => {
      attemptRef.current += 1;
    },
    [],
  );

  const settle = (attempt, next) => {
    if (attemptRef.current === attempt) setState({ ...PICKING, ...next });
  };

  const read = async (raw) => {
    attemptRef.current += 1;
    const attempt = attemptRef.current;
    setState({ ...PICKING, phase: PDF_PHASES.READING });
    try {
      const picked = {
        content: await readFileAsBase64(raw),
        name: raw.name,
        contentType: raw.type || PDF_CONTENT_TYPE,
      };
      const result = await detectAnchors({ variables: { file: picked } });
      const detected = get(result, "data.detectDocumentAnchors");
      if (isNil(detected))
        return settle(attempt, { phase: PDF_PHASES.READ_FAILED });
      if (isEmpty(zonesOf(detected)))
        return settle(attempt, { phase: PDF_PHASES.NO_ZONE });
      settle(attempt, {
        phase: PDF_PHASES.READY,
        file: picked,
        anchors: detected,
      });
      if (attemptRef.current === attempt) onReady(picked);
    } catch (_error) {
      settle(attempt, { phase: PDF_PHASES.READ_FAILED });
    }
    return undefined;
  };

  const beforeUpload = (raw) => {
    read(raw);
    return false;
  };

  const reset = () => {
    attemptRef.current += 1;
    setState(PICKING);
  };

  const attempt = () => attemptRef.current;

  return { ...state, beforeUpload, reset, attempt };
};

export const PdfRetry = ({ className, message, label, onUpload }) => (
  <div className={className}>
    <p>{message}</p>
    <Upload
      accept={PDF_ACCEPT}
      beforeUpload={onUpload}
      showUploadList={false}
      maxCount={1}
    >
      <Button type="primary--outlined">{label}</Button>
    </Upload>
  </div>
);
